/**
 * InputArea Component - Message Composition and Sending
 *
 * Provides a text input field for composing and sending messages with:
 * - Multi-line support (auto-expand up to max rows)
 * - Enter to send, Shift+Enter for new line
 * - Send button with loading state
 * - Character validation
 *
 * Future enhancements:
 * - @mention highlighting
 * - Character count indicator
 * - Typing indicators
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, TextField, IconButton, CircularProgress, Tooltip, Chip } from '@mui/material';
import { Send as SendIcon, SmartToy as SmartToyIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useTypingStatus } from '../../hooks/useTypingStatus';
import './InputArea.css';

/**
 * InputArea Component
 *
 * @param {Object} props
 * @param {Function} props.onSendMessage - Callback to send message (async)
 * @param {boolean} [props.disabled=false] - Whether input is disabled
 * @param {string} [props.placeholder='Type a message...'] - Input placeholder
 * @param {string} props.tripId - Trip ID for typing status
 * @param {string} [props.ownerId] - Trip owner ID (for shared trips)
 */
const InputArea = ({ onSendMessage, disabled = false, placeholder = 'Type a message...', tripId, ownerId = null }) => {
  const [inputValue, setInputValue] = useState('');
  const [posting, setPosting] = useState(false);
  const [showMentionHint, setShowMentionHint] = useState(false);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const { broadcastTyping } = useTypingStatus(tripId, ownerId);

  /**
   * Extract page context from URL pathname
   */
  const extractPageContext = useCallback(() => {
    const pathname = location.pathname;

    // Parse route to determine current page context
    const dayMatch = pathname.match(/\/days\/([^/]+)/);
    const placeMatch = pathname.match(/\/places\/([^/]+)/);
    const accomMatch = pathname.match(/\/accommodation\/([^/]+)/);
    const travelMatch = pathname.match(/\/travel\/([^/]+)/);

    if (dayMatch) {
      return {
        pageName: 'Day Details',
        entityType: 'day',
        entityId: dayMatch[1]
      };
    } else if (placeMatch) {
      return {
        pageName: 'Place Details',
        entityType: 'place',
        entityId: placeMatch[1]
      };
    } else if (accomMatch) {
      return {
        pageName: 'Accommodation Details',
        entityType: 'accommodation',
        entityId: accomMatch[1]
      };
    } else if (travelMatch) {
      return {
        pageName: 'Travel Details',
        entityType: 'travel',
        entityId: travelMatch[1]
      };
    } else if (pathname.includes('/trips/')) {
      return {
        pageName: 'Trip Overview',
        entityType: 'trip',
        entityId: null
      };
    }

    return {
      pageName: 'Discussion',
      entityType: null,
      entityId: null
    };
  }, [location.pathname]);

  /**
   * Handle input change
   */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Show mention hint if typing @ or @AI
    const showHint = value.endsWith('@') || value.toLowerCase().includes('@ai');
    setShowMentionHint(showHint);

    // Debounced typing status broadcast
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Broadcast typing immediately
    broadcastTyping(true);

    // Clear typing status after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      broadcastTyping(false);
    }, 3000);
  };

  /**
   * Handle key press (Enter to send, Shift+Enter for new line)
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Handle Quick AI Mention button click
   */
  const handleQuickAIMention = () => {
    setInputValue(prev => prev + '@AI_Agent ');
    setShowMentionHint(false);
    inputRef.current?.focus();
  };

  /**
   * Send message
   */
  const handleSend = async () => {
    const trimmedValue = inputValue.trim();

    // Validation
    if (!trimmedValue || posting || disabled) {
      return;
    }

    setPosting(true);

    // Clear typing status
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    broadcastTyping(false);

    try {
      // Extract page context for AI processing
      const pageContext = extractPageContext();

      await onSendMessage(trimmedValue, pageContext);
      setInputValue(''); // Clear input on success
      setShowMentionHint(false);
    } catch (error) {
      console.error('[InputArea] Error sending message:', error);
      // Error handling delegated to parent (via hook)
      // Input value preserved so user can retry
    } finally {
      setPosting(false);
    }
  };

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      broadcastTyping(false);
    };
  }, [broadcastTyping]);

  const canSend = inputValue.trim().length > 0 && !posting && !disabled;

  return (
    <Box
      sx={{
        padding: '12px 16px',
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'relative'
      }}
    >
      {/* Mention Hint */}
      {showMentionHint && (
        <Chip
          label="Type @AI_Agent to ask the AI assistant"
          size="small"
          className="mention-hint"
          color="primary"
          variant="outlined"
        />
      )}

      {/* Input Row */}
      <Box className="input-row">
        {/* Quick AI Mention Button */}
        <Tooltip title="Mention AI Assistant">
          <IconButton
            onClick={handleQuickAIMention}
            className="quick-ai-button"
            size="small"
          >
            <SmartToyIcon />
          </IconButton>
        </Tooltip>

        {/* Text Input */}
        <TextField
          inputRef={inputRef}
          multiline
          maxRows={4}
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Type a message... (use @AI_Agent to ask the AI)"
          disabled={disabled || posting}
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              paddingRight: '4px',
              backgroundColor: 'background.paper'
            },
            '& .MuiOutlinedInput-input': {
              fontSize: '0.9375rem'
            }
          }}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={handleSend}
                disabled={!canSend}
                color="primary"
                size="small"
                sx={{
                  alignSelf: 'flex-end',
                  marginBottom: '4px'
                }}
              >
                {posting ? (
                  <CircularProgress size={24} />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            )
          }}
        />
      </Box>
    </Box>
  );
};

export default InputArea;
